import { Component, createContext, lazy, Suspense, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { HuddleError, HuddleService, type HuddleCredentials, type HuddleErrorCode, type HuddleStatus } from '../../services/HuddleService';
import { supportsBrowserHuddles, type HuddleConversation } from './huddleConversation';
import { isHuddleWindow, openHuddleWindow } from './huddleLinks';
import { useOptionalCall } from './CallContext';
import { HuddlePanel } from './HuddlePanel';
import type { HuddleDeviceSelection, HuddlePrejoinChoices } from './HuddleMedia';
import type { HuddleCameraPreferences } from './HuddleCamera';
import type { User } from '../../types/auth';

const HuddleMeeting = lazy(() => import('./HuddleMeeting'));
export type HuddleChannel = HuddleConversation;
interface HuddleContextValue {
  activeChatId: string | null;
  isInHuddle: boolean;
  openHuddle: (channel: HuddleChannel) => void;
}
interface HuddleSession {
  id: number;
  channel: HuddleChannel;
  credentials: HuddleCredentials;
  microphone: boolean;
  camera: boolean;
  deviceIds: HuddleDeviceSelection;
  cameraPreferences?: HuddleCameraPreferences;
}
const HuddleContext = createContext<HuddleContextValue | null>(null);
export function useOptionalHuddle(): HuddleContextValue | null { return useContext(HuddleContext); }

class MeetingBoundary extends Component<{ children: ReactNode; onFailure: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onFailure(); }
  render() { return this.state.failed ? null : this.props.children; }
}

export function HuddleProvider({ children, currentUser }: { children: ReactNode; currentUser?: Pick<User, 'name' | 'displayName' | 'avatar'> }) {
  const call = useOptionalCall();
  const callRef = useRef(call);
  callRef.current = call;
  const [channel, setChannel] = useState<HuddleChannel | null>(null);
  const [status, setStatus] = useState<HuddleStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<HuddleError | null>(null);
  const [microphone, setMicrophone] = useState(false);
  const [camera, setCamera] = useState(false);
  const [deviceIds, setDeviceIds] = useState<HuddleDeviceSelection>({});
  const [session, setSession] = useState<HuddleSession | null>(null);
  const [minimized, setMinimized] = useState(false);
  const channelRef = useRef<HuddleChannel | null>(null);
  const sessionRef = useRef<HuddleSession | null>(null);
  const joiningRef = useRef(false);
  const generation = useRef(0);
  const requestRef = useRef<AbortController | null>(null);
  const ownsCallLock = useRef(false);

  const releaseCall = useCallback(() => {
    if (!ownsCallLock.current) return;
    ownsCallLock.current = false;
    callRef.current?.setExternalCallActive(false);
  }, []);
  const cancelRequest = useCallback(() => {
    generation.current += 1;
    requestRef.current?.abort();
    requestRef.current = null;
    joiningRef.current = false;
  }, []);
  const loadStatus = useCallback(async (nextChannel: HuddleChannel) => {
    cancelRequest();
    const requestId = generation.current;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setStatus(null);
    setError(null);
    try {
      const nextStatus = await HuddleService.getStatus(nextChannel.id, controller.signal);
      if (generation.current !== requestId) return;
      setStatus(nextStatus);
    } catch (failure) {
      if (generation.current !== requestId) return;
      setError(failure instanceof HuddleError ? failure : new HuddleError('unavailable'));
    } finally {
      if (generation.current === requestId) { setLoading(false); requestRef.current = null; }
    }
  }, [cancelRequest]);

  const openHuddle = useCallback((nextChannel: HuddleChannel) => {
    // In a browser the huddle gets a window of its own; inside that window, or
    // if the browser blocks the window, it opens here as before.
    if (supportsBrowserHuddles() && !isHuddleWindow() && openHuddleWindow(nextChannel.id)) return;
    if (sessionRef.current) { setMinimized(false); return; }
    if (joiningRef.current) return;
    channelRef.current = nextChannel;
    setChannel(nextChannel);
    setMicrophone(false);
    setCamera(false);
    setDeviceIds({});
    if (!supportsBrowserHuddles()) {
      cancelRequest(); setStatus(null); setLoading(false); setError(new HuddleError('native_unavailable')); return;
    }
    void loadStatus(nextChannel);
  }, [cancelRequest, loadStatus]);

  const cancelPanel = useCallback(() => {
    if (isHuddleWindow()) { window.close(); return; }
    cancelRequest();
    releaseCall();
    channelRef.current = null;
    setChannel(null);
    setJoining(false);
    setLoading(false);
    setError(null);
  }, [cancelRequest, releaseCall]);

  const join = useCallback(async (choices: HuddlePrejoinChoices) => {
    const nextChannel = channelRef.current;
    if (!nextChannel || !status?.configured || loading || joiningRef.current || sessionRef.current) return;
    if (!supportsBrowserHuddles()) { setError(new HuddleError('native_unavailable')); return; }
    if (callRef.current?.status && callRef.current.status !== 'idle') { setError(new HuddleError('call_active')); return; }
    if (status.participantCount >= status.maxParticipants) { setError(new HuddleError('room_full')); return; }
    cancelRequest();
    joiningRef.current = true;
    ownsCallLock.current = true;
    callRef.current?.setExternalCallActive(true);
    setJoining(true);
    setError(null);
    const requestId = generation.current;
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const credentials = await HuddleService.join(nextChannel.id, controller.signal);
      if (generation.current !== requestId) return;
      const nextSession = { id: requestId, channel: nextChannel, credentials, ...choices };
      sessionRef.current = nextSession;
      setSession(nextSession);
      setMinimized(false);
      channelRef.current = null;
      setChannel(null);
    } catch (failure) {
      if (generation.current !== requestId) return;
      releaseCall();
      setError(failure instanceof HuddleError ? failure : new HuddleError('unavailable'));
    } finally {
      if (generation.current === requestId) { joiningRef.current = false; setJoining(false); requestRef.current = null; }
    }
  }, [cancelRequest, loading, releaseCall, status]);

  const leave = useCallback(() => {
    cancelRequest();
    sessionRef.current = null;
    setSession(null);
    setMinimized(false);
    releaseCall();
  }, [cancelRequest, releaseCall]);
  // Leaving a huddle that has its own window closes that window.
  const leaveHuddle = useCallback(() => {
    leave();
    if (isHuddleWindow()) window.close();
  }, [leave]);
  const meetingFailed = useCallback((sessionId: number, code: HuddleErrorCode = 'unavailable') => {
    const current = sessionRef.current;
    if (!current || current.id !== sessionId) return;
    leave();
    channelRef.current = current.channel;
    setChannel(current.channel);
    setError(new HuddleError(code));
  }, [leave]);
  useEffect(() => () => { cancelRequest(); releaseCall(); sessionRef.current = null; }, [cancelRequest, releaseCall]);

  // While a huddle's lobby or call is open the address says so, using the same
  // ?huddle= form as a shared link, so a reload or a copied URL brings it back.
  // Only a parameter this effect added is removed, never one a link brought in.
  const shownHuddleId = channel?.id ?? session?.channel.id ?? null;
  const addedHuddleParam = useRef(false);
  useEffect(() => {
    const url = new URL(window.location.href);
    const current = url.searchParams.get('huddle');
    if (shownHuddleId && current !== shownHuddleId) {
      url.searchParams.set('huddle', shownHuddleId);
      addedHuddleParam.current = true;
    } else if (!shownHuddleId && current && addedHuddleParam.current) {
      url.searchParams.delete('huddle');
      addedHuddleParam.current = false;
    } else return;
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [shownHuddleId]);

  return (
    <HuddleContext.Provider value={{ activeChatId: session?.channel.id ?? (joining ? channel?.id ?? null : null), isInHuddle: joining || session !== null, openHuddle }}>
      {children}
      {channel && <HuddlePanel key={channel.id} channelName={channel.name} conversationKind={channel.kind} status={status} loading={loading} joining={joining} error={error}
        identity={currentUser ? { name: currentUser.displayName || currentUser.name, avatar: currentUser.avatar } : undefined}
        microphone={microphone} camera={camera} onMicrophoneChange={setMicrophone} onCameraChange={setCamera}
        deviceIds={deviceIds} onDeviceIdsChange={setDeviceIds}
        onJoin={(choices) => { void join(choices); }} onRetry={() => { void loadStatus(channel); }} onCancel={cancelPanel} />}
      {session && <MeetingBoundary key={session.id} onFailure={() => meetingFailed(session.id)}>
        <Suspense fallback={<div role="status" className="fixed bottom-6 right-6 z-[250] rounded-xl border border-border bg-background p-4 text-sm shadow-lg">Preparing the huddle… <button className="ml-3 underline" onClick={leaveHuddle}>Cancel</button></div>}>
          <HuddleMeeting credentials={session.credentials} channelName={session.channel.name} conversationKind={session.channel.kind} microphone={session.microphone} camera={session.camera}
            deviceIds={session.deviceIds} cameraPreferences={session.cameraPreferences}
            minimized={minimized} onMinimize={() => setMinimized(true)} onRestore={() => setMinimized(false)} onLeave={leaveHuddle}
            onDisconnected={(code) => meetingFailed(session.id, code)} onError={(code) => meetingFailed(session.id, code)} />
        </Suspense>
      </MeetingBoundary>}
    </HuddleContext.Provider>
  );
}
